import { CommonEnums } from 'dashboard-services/api/common';
import { DataStorageEnums, GetDataBody, Key, SymbolSearchQuery, SymbolSearchSortBy } from 'dashboard-services/api/dataStorage';
import { DataTransformEnums } from 'dashboard-services/api/dataTransform';
import { ActionService, CommonService, ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { globalActions } from 'primary-components';

import { basicCustomInsightActions, basicCustomInsightConsts, chartCustomInsightActions, inputParamsCustomInsightActions, tableCustomInsightActions } from './';

import dayjs from 'dayjs';

export default (() => {
  const ON_CHANGE_BASKET = "ON_CHANGE_BASKET_CUSTOM_INSIGHT_ACTIONS"
  const onChangeBasket = ActionService.makeActionCreator(ON_CHANGE_BASKET, 'value', 'name', 'basketId')
  
  const ADD_TO_BASKET = "ADD_TO_BASKET_CUSTOM_INSIGHT_ACTIONS"
  const addToBasket = ActionService.makeActionCreator(ADD_TO_BASKET, 'item', 'basketId')

  const REMOVE_FROM_BASKET = "REMOVE_FROM_BASKET_CUSTOM_INSIGHT_ACTIONS"
  const removeFromBasket = ActionService.makeActionCreator(REMOVE_FROM_BASKET, 'index', 'basketId')

  const getIndexOfOneSymbolInBasket = (symbol, selectedSymbols) => selectedSymbols.findIndex(sel => JSON.stringify(sel.symbols) === JSON.stringify(symbol.symbols) && sel.groupName === symbol.groupName && sel.pattern === false)
  const getIndexOfPatternSymbolInBasket = (symbol, selectedSymbols) => selectedSymbols.findIndex(sel => JSON.stringify(sel.symbols) === JSON.stringify(symbol.symbols) && sel.groupName === symbol.groupName && sel.query === symbol.query && JSON.stringify(sel.metadata) === JSON.stringify(symbol.metadata))

  const isMatchingQuery = ({ query, value = "" }) => value.match(query)
  const isMatchingQueryObj = ({ query, value = {} }) => Object.values(value).some(v => isMatchingQuery({ query, value: v }))

  const isInBasket = (symbol, selectedSymbols = []) => {
    const isDirectlySelected = selectedSymbols.findIndex(sel => {
      if(sel.pattern === false) {
        return JSON.stringify(sel.symbols) === JSON.stringify(symbol.symbols) && sel.groupName === symbol.groupName 
      }
      return false;
    }) > -1
    const isPatternSelected = selectedSymbols.findIndex(sel => {
      if(sel.pattern !== false) {
        if(sel.groupName !== symbol.groupName && sel.groupName) {
          return false;
        }
        const symbolMatch = !Object.entries(sel?.symbols || {}).some(([key, value]) => symbol.symbols[key] !== value && value !== "*" && value !== undefined);
        const metadataMatch = !Object.entries(sel?.metadata || {}).some(([key, value]) => symbol.metadata[key] !== value && value !== "*" && value !== undefined);
        if(symbolMatch && metadataMatch) {
          if(sel.query) {
            const matchQuery = (isMatchingQuery({ query: sel.query, value: sel.groupName }) || isMatchingQueryObj({ query: sel.query, val: sel.symbols }) || isMatchingQueryObj({ query: sel.query, value: sel.metadata }))
            return matchQuery;
          }
          return true;
        }
        return false;
      }
      return false;
    }) > -1
    const isReadOnlySelected = selectedSymbols.some(sel => sel.previewAccess === true)

    return {
      isDirectlySelected,
      isPatternSelected,
      isReadOnlySelected
    }
  }

  const SWAP_ITEM_IN_BASKET = 'SWAP_ITEM_IN_BASKET_BASKET_CUSTOM_INSIGHT_ACTIONS'
  const swapItemInBasket = (symbol, basketId) => (dispatch, getState) => {
    if(symbol.pattern === false) {
      const index = getIndexOfOneSymbolInBasket(symbol, getState().insightState.customState.basket[basketId]?.items || [])
      const localSymbol = ReduxService.updateObject(symbol, { pattern: false })
      dispatch(swapItemInBasketReducer(localSymbol, index, basketId))
    } else {
      // only for removing
      const index = getIndexOfPatternSymbolInBasket(symbol, getState().insightState.customState.basket[basketId]?.items || [])
      if(index === -1) {
        return;
      }
      dispatch(swapItemInBasketReducer(symbol, index, basketId))
    }
  }
  const swapItemInBasketReducer = ActionService.makeActionCreator(SWAP_ITEM_IN_BASKET, 'item', 'itemIndex', 'basketId')

  const getSymbolsFilter = ({ filters, userDefinedFilters }) => {
    const toReturn = {}
    const notMd = userDefinedFilters.filter(udf => udf.type === FILTER_TYPE.SYMBOL).filter(udf => filters[udf.filterKey] !== undefined || (typeof filters[udf.filterKey] !== "string" && filters[udf.filterKey]?.length !== 0))
    if(notMd.length === 0) {
      return undefined;
    }
    notMd.forEach(udf => {
      const value = typeof filters[udf.filterKey] === "string" ? [filters[udf.filterKey]] : (filters[udf.filterKey] || []).map(f => f?.value || f).filter(v => !!v)
      toReturn[udf.filterKey] = value?.length === 0 ? undefined : value
    })
    return toReturn
  }

  const getMetadatasFilter = ({ filters, userDefinedFilters }) => {
    const toReturn = {}
    const mds = userDefinedFilters.filter(udf => udf.type === FILTER_TYPE.METADATA).filter(udf => filters[udf.filterKey] !== undefined || (typeof filters[udf.filterKey] !== "string" && filters[udf.filterKey]?.length !== 0))
    if(mds.length === 0) {
      return undefined;
    }
    mds.forEach(udf => {
      const value = typeof filters[udf.filterKey] === "string" ? [filters[udf.filterKey]] : (filters[udf.filterKey] || []).map(f => f?.value || f).filter(v => !!v)
      toReturn[udf.filterKey] = value?.length === 0 ? undefined : value
    })
    return toReturn
  }

  const getGroupFilter = ({ filters, userDefinedFilters }) => {
    const groupName = userDefinedFilters.find(udf => udf.type === FILTER_TYPE.GROUP_NAME)
    return groupName ? filters[groupName.filterKey] : undefined
  }

  // A group filter value is a string (single select, also older insights) or an array (multi select)
  const getGroupNamesFromFilter = value => [].concat(value).filter(Boolean)

  // Source groups are the whole scope of the tile: the group filter only narrows it, an empty filter means all Source groups
  const getSelectedGroupNames = ({ groupFilter, groupNames }) => {
    const sourceGroupNames = groupNames || [],
          filterGroupNames = getGroupNamesFromFilter(groupFilter),
          scopedGroupNames = sourceGroupNames.length > 0 ? filterGroupNames.filter(groupName => sourceGroupNames.includes(groupName)) : filterGroupNames
    return scopedGroupNames.length > 0 ? scopedGroupNames : sourceGroupNames
  }

  // /envs keys and values: one group -> groupName, many groups -> groupNames, no groups -> whole environment
  const getGroupNameParams = ({ groupNames = [] }) =>
    groupNames.length > 1
      ? { groupNames }
      : groupNames.length === 1 ? { groupName: groupNames[0] } : {}

  // A default filter value as a flat array for symbols / metadatas, an empty value only requires the key to exist
  const getFilterValues = ({ value }) => {
    const values = [].concat(value ?? []).filter(v => v !== "")
    return values.length > 0 ? values : ["*"]
  }

  // Patterns cover the same groups as the results: the group filter narrowed to the tile source groups, or all of them.
  // A basket key holds one groupName, so every group gets its own pattern
  const createPattern = ({ query, filters, userDefinedFilters, basketId, groupNames }) => dispatch => {
    const key = {
            query,
            symbols: getSymbolsFilter({ filters, userDefinedFilters }) || {},
            metadata: getMetadatasFilter({ filters, userDefinedFilters }) || {},
            pattern: true
          },
          selectedGroupNames = getSelectedGroupNames({ groupFilter: getGroupFilter({ filters, userDefinedFilters }), groupNames }),
          patternGroupNames = selectedGroupNames.length > 0 ? selectedGroupNames : [undefined]
    // now we need all comibinations of symbols and metadata
    const combinations = basicCustomInsightActions.generateKeysCombinations({ symbols: key.symbols, metadatas: key.metadata })
    patternGroupNames.forEach(groupName => combinations.forEach(({ symbols, metadatas: metadata }) => {
      dispatch(swapItemInBasketReducer({
        ...key,
        groupName,
        symbols,
        metadata
      }, -1, basketId))
    }))
  }

  const FILTER_TYPE = {
    GROUP_NAME: "groupName",
    METADATA: DataStorageEnums.KEY_TYPE.METADATA,
    SYMBOL: DataStorageEnums.KEY_TYPE.SYMBOL
  }

  const getSortString = userDefinedFilters => {
    const builder = userDefinedFilters.filter(udf => !!udf.filterKey).reduce((prev, curr) => {
      if(curr) {
        if(curr.type === FILTER_TYPE.GROUP_NAME) {
          return prev
        }
        return curr.type === FILTER_TYPE.METADATA ? prev.withMetadataSort(curr.filterKey) : prev.withSort(curr.filterKey)
      }
      return prev;
    }, new SymbolSearchSortBy.Builder())
    return builder.withSort("Symbol").build().getSortBy()
  }

  const getSearchQuery = ({ query, groupName, symbolsFilter = {}, metadatasFilter = {}, groupNames, isFormulaSourceEnabled = false }) => {
    const symbols = Object.entries(symbolsFilter).filter(([k, v]) => !!k && (typeof v !== "string" ? v?.length > 0 : !!v)).map(([key, value]) => ({ key, value }))
    let builder = new SymbolSearchQuery.Builder()
      .withSymbols(symbols)
      .withMetadatas(Object.entries(metadatasFilter).filter(([k, v]) => !!k && (typeof v !== "string" ? v?.length > 0 : !!v)).map(([key, value]) => ({ key, value })))
      .withQueryString(query ? `${query}*` : "")
    if(isFormulaSourceEnabled && !symbols.some(({ key }) => key === "Symbol")) {
      builder = builder.withSymbol("Symbol", "*")
    }
    if(groupName || typeof groupName === "string") {
      builder = builder.withGroupName(`*${String(groupName).toLowerCase()}*`, false)
    }
    if(groupNames?.length > 0) {
      // Exact match on groupName.keyword - the analyzed groupName field would also match e.g. "a-b-c" for "a-b"
      builder = builder.withGroupName(groupNames)
    }
    return builder
      .build()
      .getQuery()
  }

  const ON_REORDER_ITEM_IN_BASKET = 'ON_REORDER_ITEM_IN_BASKET_CUSTOM_INSIGHT_ACTIONS'
  const onReorderItem = ActionService.makeActionCreator(ON_REORDER_ITEM_IN_BASKET, 'from', 'to', 'pattern', 'basketId')

  const BASKET_TABS = {
    DATA: "Data",
    SETTINGS: "Settings"
  }

  const getBody = (basketId, { items: itemsOverride } = {}) => (_, getState) => {
    const tileConfig = getState().insightState.customState.content?.tiles?.find(t => t.id === basketId),
    { 
      items: basketItems, 
      range = tileConfig.range,
      from = tileConfig.from,
      fromSelected = tileConfig.fromSelected,
      toSelected = tileConfig.toSelected,
      fromTime = tileConfig.fromTime,
      toTime = tileConfig.toTime,
      to = tileConfig.to,
      lastType = tileConfig.lastType,
      lastTypeAmount = tileConfig.lastTypeAmount,
      dateFormat = tileConfig.dateFormat,
      timeZone = tileConfig.timeZone,
      columns = tileConfig.columns,
    } = getState().insightState.customState.basket[basketId] || {}
    // Items given by the caller, e.g. with the environment of every key (source) for the /envs requests
    const items = itemsOverride || basketItems
    if(!items) {
      return undefined;
    }
    let builder = new GetDataBody.Builder()
    items.forEach(basketItem => {
      const selectedColumns = (basketItem.columns?.length ? basketItem.columns : columns?.length ? columns : tileConfig.columns || []).map(column => column?.value || column)
      const key = new Key.Builder()
        .withColumns(selectedColumns.findIndex(c => c === "*") === -1 ? selectedColumns : [])
        .withSymbols(basketItem.symbols)
        .withMetadatas(basketItem.pattern ? basketItem.metadata : undefined)
        .withGroupName(basketItem.groupName)
        .withType(basketItem.type)
        .withPattern(basketItem.pattern)
        .withPatternExactMatch(basketItem.patternExactMatch)
        .withSymbolValuesExactMatch(basketItem.symbolValuesExactMatch)
        .withSource(basketItem.source)
      builder.withKey(key.build().serialize())
    })
    if(range === DataStorageEnums.DATA_RANGE.BETWEEN) {
      builder = builder
              .withStartDate(fromSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE ? (from ? dayjs(from + (fromTime ? "T" + fromTime : "T00:00:00.000")).format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined) : fromSelected)
              .withEndDate(toSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE ? (to ? dayjs(to  + (toTime ? "T" + toTime : "T23:59:59.999")).format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined) : toSelected)
    }

    if(range === DataStorageEnums.DATA_RANGE.LAST || range === DataStorageEnums.DATA_RANGE.NEXT) {
      builder = builder
              .withLastType(lastType)
              .withLastTypeAmount(lastTypeAmount)
    }

    const body = builder
      .withRange(range)
      .withDateFormat(dateFormat)
      .withTimeZone(timeZone)
    return body
  }

  // With All providers every key needs its environment: a selected result has its source,
  // a pattern goes to every environment its group was selected from in the tile source options
  const getItemsWithSources = ({ items = [], tileConfig }) =>
    items.flatMap(item => getItemSources({ item, groupName: item.groupName, tileConfig, isAllProviders: true }).map(source => ReduxService.updateObject(item, { source })))

  // Charts and statistics ask /ts on one environment only: the selected provider (Target) or, for All and no provider, the local one.
  // /ts has no /envs version and data of several environments is not joined on the front end, so All is not supported here.
  // chart getTileData sends it as Target and gives every series this environment, it decides which groups get insight roles
  const getTsProvider = basketId => (_, getState) =>
    basicCustomInsightConsts.getProviderTarget(getState().insightState.customState.content?.tiles?.find(t => t.id === basketId)?.provider)

  const executeCharts = ({ basketId, chartTiles, tiles, withMessage, getDataCall }) => dispatch => {
    let body = dispatch(getBody(basketId))
      if(!body) {
        return;
      }
      body = body
        .withFormatType(DataStorageEnums.DATA_FORMAT.UI)
        .build()
        .serialize()
      const requestIds = inputParamsCustomInsightActions.getRequestIds({ specificTiles: chartTiles, prefix: basicCustomInsightConsts.TILE_TYPES.CHART, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ symbols: [], values: [], isError: true }, "chart", basketId))
      }
      const onComplete = ({ symbols, values }) => {
        dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ symbols, values, isError: false }, "chart", basketId))
        if(withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      chartCustomInsightActions.getTileData({ onHandleRequest, body, onComplete, getDataCall, onError, provider: dispatch(getTsProvider(basketId)) })
  }

  const getCurveConfig = ({ basket = {}, tileConfig = {} }) => ({
    ...tileConfig,
    ...basket,
    contractSort: tileConfig.contractSort || CommonEnums.SORT_ORDER.ASCENDING,
    csvHeaders: tileConfig.csvHeaders || basicCustomInsightConsts.DEFAULT_CSV_HEADERS_CURVE_TABLE,
    fillBlanks: tileConfig.fillBlanks,
    isRelative: tileConfig.isRelative ?? "false",
    periodType: tileConfig.periodType || DataTransformEnums.PERIOD_TYPE.MONTH,
    symbolSize: tileConfig.symbolSize
  })

  const getConfiguredColumns = ({ item, tileConfig }) => {
    const configuredColumn = tileConfig.column?.value || tileConfig.column?.label || tileConfig.column

    return item.columns?.length
      ? item.columns
      : tileConfig.columns?.length
        ? tileConfig.columns
        : configuredColumn
          ? [configuredColumn]
          : []
  }

  // Environments a basket key is asked on with All providers: the source of a selected result,
  // or every environment the group was selected from in the tile source options (patterns, whole groups)
  const getItemSources = ({ item, groupName, tileConfig, isAllProviders }) => {
    if(!isAllProviders) {
      return [undefined]
    }
    if(item.source) {
      return [item.source]
    }
    const groupSources = (tileConfig.groupSources || [])
      .filter(groupSource => groupSource.groupName === groupName && groupSource.source)
      .map(groupSource => groupSource.source)
    return groupSources.length > 0 ? groupSources : [undefined]
  }

  // The curve CSV has no environment, so a chart column gets the provider of the tile (charts cannot use All providers).
  // It is saved with the chart symbols and decides which groups get insight roles
  const getCurveColumnsWithSources = ({ columns, tileConfig }) => {
    const providerTarget = basicCustomInsightConsts.getProviderTarget(tileConfig.provider)

    return providerTarget ? columns.map(column => ReduxService.updateObject(column, { sources: [providerTarget] })) : columns
  }

  // Basket items as curve keys (root x group x column, with All providers also x environment).
  // Shared by the tile data and the saved formula, so a formula calculates on the same curves as the table
  const getCurveItems = ({ items = [], tileConfig, isAllProviders }) =>
    items.flatMap(item => {
      const root = item.metadata?.[basicCustomInsightConsts.FORWARD_CURVE_KEY.ROOT]
            || item.symbols?.[basicCustomInsightConsts.FORWARD_CURVE_KEY.ROOT]
            || item.symbols?.[basicCustomInsightConsts.FORWARD_CURVE_KEY.SYMBOL]
            || item[basicCustomInsightConsts.FORWARD_CURVE_KEY.ROOT],
            groupNames = item.groupName ? [item.groupName] : tileConfig.groupNames || [],
            columns = getConfiguredColumns({ item, tileConfig })
      return (Array.isArray(root) ? root : [root]).flatMap(curveRoot => groupNames.flatMap(groupName => getItemSources({ item, groupName, tileConfig, isAllProviders }).flatMap(source => columns.map(column => ({ root: curveRoot?.value || curveRoot, groupName, column: column?.value || column, source })))))
    })

  const executeCurves = ({ basketId, chartTiles, tableTiles, tiles, withMessage, getCurvesDataCall, tileConfig, items }) => dispatch => {
    const isAllProviders = basicCustomInsightConsts.isAllProviders(tileConfig.provider),
          curveKeys = inputParamsCustomInsightActions.getCurveBodyKeys({ items: getCurveItems({ items, tileConfig, isAllProviders }) })
    if(curveKeys.length === 0) {
      dispatch(globalActions.getMessageStore()).error("Select at least one curve and column.")
      return;
    }

    const runCurveRequest = ({ onComplete, onError, requestTiles, prefix, header, csvHeaders, preparsing }) => {
      const requestIds = inputParamsCustomInsightActions.getRequestIds({ specificTiles: requestTiles, prefix, tiles }),
            onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse }))),
            curveBody = ReduxService.updateObject(
              inputParamsCustomInsightActions.getCurveBody({ ...tileConfig, keys: curveKeys, csvHeaders }),
              isAllProviders ? { useEnvs: true } : { provider: basicCustomInsightConsts.getProviderTarget(tileConfig.provider) }
            )
      dispatch(inputParamsCustomInsightActions.getCurveData({ curveBody, getCurvesDataCall, onHandleRequest, onComplete, onError, header, preparsing }))
    }

    if(tableTiles.length > 0) {
      const onError = () => dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ columns: [], data: [], isError: true }, "table", basketId)),
            onComplete = ({ columns, data }) => {
              dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ columns, data, isError: false }, "table", basketId))
              if(chartTiles.length === 0 && withMessage) {
                dispatch(globalActions.getMessageStore()).success("Parameters applied successfully")
              }
            }
      runCurveRequest({
        onComplete,
        onError,
        requestTiles: tableTiles,
        prefix: basicCustomInsightConsts.TILE_TYPES.TABLE,
        header: true,
        csvHeaders: tileConfig.csvHeaders || basicCustomInsightConsts.DEFAULT_CSV_HEADERS_CURVE_TABLE,
        preparsing: args => inputParamsCustomInsightActions.parseResultForTable(args)
      })
    }
    if(chartTiles.length > 0) {
      const onError = () => {
              dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ symbols: [], values: [], isError: true }, "chart", basketId))
              dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ columns: [], data: [], filteredData: [], isError: true }, "fileData", basketId))
            },
            onComplete = ({ data }) => {
              const { data: values, columns } = inputParamsCustomInsightActions.parseResultsForChart({ ...tileConfig, newResult: data }),
                    symbols = getCurveColumnsWithSources({ columns, tileConfig }),
                    // Forward curve charts read the same data as with curve input params (fileData), the other chart types read "chart"
                    { data: fileData, columns: fileColumns } = inputParamsCustomInsightActions.parseResultsForChart({ ...tileConfig, newResult: data, labelDateFormat: "" })
              dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ symbols, values, isError: false }, "chart", basketId))
              dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ columns: fileColumns, data: fileData, filteredData: fileData, isError: false }, "fileData", basketId))
              if(withMessage) {
                dispatch(globalActions.getMessageStore()).success("Parameters applied successfully")
              }
            }
      runCurveRequest({
        onComplete,
        onError,
        requestTiles: chartTiles,
        prefix: basicCustomInsightConsts.TILE_TYPES.CHART,
        header: false,
        csvHeaders: basicCustomInsightConsts.DEFAULT_CSV_HEADERS_CURVE_CHART
      })
    }
  }

  const getChartDataToDownload = ({ basketId, getDataCall, csv }) => dispatch => {
    const onComplete = response => {
      CommonService.createAndDownload(response, "data.csv")
    }
    const onError = () => {
      dispatch(globalActions.getMessageStore()).error("Could not find any data to download")
    }
    let body = dispatch(getBody(basketId))
    if(!body) {
      onError()
      return Promise.resolve()
    }
    body = body
      .withFormatType(DataStorageEnums.DATA_FORMAT.NCSV)
      .build()
      .serialize()
    
    return chartCustomInsightActions.getTileData({ body, onComplete, getDataCall, onError, csv, provider: dispatch(getTsProvider(basketId)) })
  }

  const executeStats = ({ basketId, statsTiles, tiles, withMessage, getDataCall }) => dispatch => {
    let body = dispatch(getBody(basketId))
      if(!body) {
        return;
      }
      body = body
        .withFormatType(DataStorageEnums.DATA_FORMAT.UI)
        .withStats(true)
        .build()
        .serialize()
      const requestIds = inputParamsCustomInsightActions.getRequestIds({ specificTiles: statsTiles, prefix: basicCustomInsightConsts.TILE_TYPES.STATS, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ symbols: {}, values: [], stats: [], metadata: {}, isError: true }, "stats", basketId))
      }
      const onComplete = ({ symbols, metadata, stats }) => {
        dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ symbols, metadata, stats, isError: false }, "stats", basketId))
        if(withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      chartCustomInsightActions.getTileData({ onHandleRequest, body, onComplete, getDataCall, onError, provider: dispatch(getTsProvider(basketId)) })
  }

  const executeTables = ({ basketId, areThereAnyChartTiles, tableTiles, tiles, withMessage }) => (dispatch, getState) => {
    const tileConfig = getState().insightState.customState.content.tiles.find(t => t.id === basketId),
          csvFormat = tileConfig?.csvFormat || DataStorageEnums.DATA_FORMAT.NCSV,
          csvHeaders = tileConfig?.csvHeaders,
          isAllProviders = basicCustomInsightConsts.isAllProviders(tileConfig?.provider),
          items = isAllProviders ? getItemsWithSources({ items: getState().insightState.customState.basket[basketId]?.items, tileConfig }) : undefined
    let body = dispatch(getBody(basketId, { items }))
    if(!body) {
      return;
    }
    body = body
      .withFormatType(csvFormat)
      .withCsvHeaders(csvFormat === DataStorageEnums.DATA_FORMAT.CSV && csvHeaders ? csvHeaders.filter(c => c).map(c => ({ name: c.value, type: c.type })) : undefined)
      .build()
      .serialize()
    const requestIds = inputParamsCustomInsightActions.getRequestIds({ specificTiles: tableTiles, prefix: basicCustomInsightConsts.TILE_TYPES.TABLE, tiles })
    const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
    const onError = () => {
      dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ columns: [], data: [], isError: true }, "table", basketId))
    }
    const onComplete = ({ columns, data }) => {
      dispatch(inputParamsCustomInsightActions.onChangeInputParamsReducer({ columns, data, isError: false }, "table", basketId))
      if(!areThereAnyChartTiles && withMessage) {
        dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
      }
    }
    dispatch(tableCustomInsightActions.getTileData({
      onHandleRequest,
      body,
      onComplete,
      onError,
      provider: basicCustomInsightConsts.getProviderTarget(tileConfig?.provider),
      useEnvs: isAllProviders
    }))
  }

  const onApply = ({ basketId, withMessage = true, getDataCall, getCurvesDataCall }) => (dispatch, getState) => {
      const tiles = getState().insightState.customState.content.tiles,
            tileConfig = tiles.find(tile => tile.id === basketId),
            basket = getState().insightState.customState.basket[basketId] || {},
            items = basket.items || [],
            tilesWithMatchingSource = tiles.filter(t => t.source?.value?.id === basketId),
            tableTiles = tilesWithMatchingSource.filter(t => t.type === basicCustomInsightConsts.TILE_TYPES.TABLE),
            chartTiles = tilesWithMatchingSource.filter(t => t.type === basicCustomInsightConsts.TILE_TYPES.CHART),
            statsTiles = tilesWithMatchingSource.filter(t => t.type === basicCustomInsightConsts.TILE_TYPES.STATS),
            areThereAnyChartTiles = chartTiles.length > 0

    if(tileConfig?.basicSearchType === basicCustomInsightConsts.BASIC_SEARCH_TYPES.CURVE.value) {
      dispatch(executeCurves({ basketId, chartTiles, tableTiles, tiles, withMessage, getCurvesDataCall, tileConfig: getCurveConfig({ tileConfig, basket }), items }))
      return
    }
            
    if(tableTiles.length > 0) {
      dispatch(executeTables({ basketId , areThereAnyChartTiles, tableTiles, tiles, withMessage }))
    }
    if(areThereAnyChartTiles) {
      dispatch(executeCharts({ basketId, chartTiles, tiles, withMessage, getDataCall }))
    }
    if(statsTiles.length > 0) {
      dispatch(executeStats({ basketId, statsTiles, tiles, withMessage, getDataCall }))
    }
  }

  const requestDataAccess = ({ dataLakeUploadCall, symbolsToRequest, searchTileTitle }) => (_, getState) => {
    const userName = getState().myAccountState.settingsState.userName,
          insightDetails = getState().insightState.customState.info,
          groupAndSymbolsMap = {},
          objectToUpload = {
            "username": userName,
            "insightName": insightDetails?.fileName?.replace(".json", "") || "",
            "insightGroup": insightDetails?.groupName || "",
            "tileTitle": searchTileTitle || "",
            "requestedData": {}
          }

    symbolsToRequest.forEach(sym => {
      const { groupName, symbols, pattern, metadata } = sym || {}

      if (!groupAndSymbolsMap[groupName]) {
        groupAndSymbolsMap[groupName] = {}
      }

      groupAndSymbolsMap[groupName] = {
        symbols,
        metadata,
        pattern
      }
    })
    
    objectToUpload.requestedData = { ...groupAndSymbolsMap }

    return dataLakeUploadCall({ image: JSON.stringify(objectToUpload) })
  }

  const DATA_REQUESTS_GROUP_NAME = "DATA_REQUESTS"

  // /envs responses list environments that failed in additionalData.errors (same handling as in the office add-in)
  const onEnvsSuccess = ({ response }) => dispatch => {
    response?.additionalData?.errors?.forEach(e => {
      if(e.statusCode === 500) {
        dispatch(globalActions.getMessageStore()).warning("Some data is currently unavailable.")
      } else {
        dispatch(globalActions.getMessageStore()).error(`source: ${e.source} ${e.message}`)
      }
    })
  }

  return {
    FILTER_TYPE,
    getSortString,
    onEnvsSuccess,
    getSearchQuery,
    ON_CHANGE_BASKET,
    onChangeBasket,
    ADD_TO_BASKET,
    addToBasket,
    REMOVE_FROM_BASKET,
    removeFromBasket,
    SWAP_ITEM_IN_BASKET,
    swapItemInBasket,
    isInBasket,
    createPattern,
    getSymbolsFilter,
    getMetadatasFilter,
    getGroupFilter,
    getGroupNamesFromFilter,
    getSelectedGroupNames,
    getGroupNameParams,
    getFilterValues,
    onReorderItem,
    ON_REORDER_ITEM_IN_BASKET,
    BASKET_TABS,
    onApply,
    requestDataAccess,
    DATA_REQUESTS_GROUP_NAME,
    getChartDataToDownload,
    getCurveConfig,
    getCurveColumnsWithSources,
    getCurveItems,
    getItemSources,
    getItemsWithSources,
    getConfiguredColumns,
    getBody
  }
})()
